// Global Instructions Rule Applied! - 2026-05-11
import type { ViewConfigColumn } from '../../core/components/ViewConfig/types.ts'
import type { GlobalConfigRecord } from '../data_models/global_config_record.ts'
import type { ReportingColumnDefaultsModel } from '../data_models/reporting_column_defaults_model.ts'
import type { ReportingColumnsModel } from '../data_models/reporting_columns_model.ts'
import { MENU_ITEM_KEY_DEALS } from '../globals.ts'
import { reportingColumnToViewConfigColumn } from './reportingGridColumns.ts'

export type GetAddColumnsCatalogOptions = {
  /**
   * Columns whose `module_key` matches go in the first tab (historically "Deal");
   * other linked modules go in the second tab ("Related"). Defaults to `deals`.
   */
  primaryModuleKey?: string
}

/**
 * Build the Add Columns catalog for a report from its curated
 * `cfg.reporting_columns` set.
 *
 * Each row in `cfg.reporting_columns` for the report — visible or hidden —
 * is a column the report supports. `visible=true` rows are the default
 * display set; `visible=false` rows are addable. Together they form the
 * complete catalog. The AddColumnsPanel uses the table's currently-
 * displayed `orderedColumns` to decide which entries render as checked
 * (already added) vs unchecked (available to add).
 *
 * Per-report `*_override` columns flow through via
 * {@link reportingColumnToViewConfigColumn}, so report-specific titles /
 * filter types / sort keys appear correctly in the catalog.
 *
 * Tabbing: columns whose underlying defaults row is on `primaryModuleKey`
 * go in the first tab; everything else in "Related". The default primary
 * is `deals`; callers pass the correct key for non-deals reports.
 */
export function getAddColumnsCatalogForReport(
  reportId: string | null | undefined,
  globalConfigs: Pick<GlobalConfigRecord, 'reporting_columns' | 'reporting_column_defaults'>,
  options?: GetAddColumnsCatalogOptions
): { deal: ViewConfigColumn[]; related: ViewConfigColumn[] } {
  const primaryModuleKey = options?.primaryModuleKey ?? MENU_ITEM_KEY_DEALS

  if (!reportId) return { deal: [], related: [] }

  const defaultsById = new Map<string, ReportingColumnDefaultsModel>()
  for (const rcd of globalConfigs.reporting_column_defaults ?? []) {
    defaultsById.set(rcd.id, rcd)
  }

  const perReportRows: ReportingColumnsModel[] = (globalConfigs.reporting_columns ?? []).filter(
    (rc) => rc.report_id === reportId
  )

  const byTitle = (a: ViewConfigColumn, b: ViewConfigColumn) =>
    a.columnTitle.localeCompare(b.columnTitle, undefined, { sensitivity: 'base' })

  const deal: ViewConfigColumn[] = []
  const related: ViewConfigColumn[] = []

  for (const pr of perReportRows) {
    const defaults = defaultsById.get(pr.reporting_column_defaults_id)
    if (!defaults) continue
    const vc = reportingColumnToViewConfigColumn(defaults, pr)
    if (defaults.module_key === primaryModuleKey) {
      deal.push(vc)
    } else {
      related.push(vc)
    }
  }

  deal.sort(byTitle)
  related.sort(byTitle)

  return { deal, related }
}
